import { useEffect, type RefObject } from "react";

/** Elements that keep their own pointer behavior (cards, controls). */
const IGNORE_SELECTOR = [
  "[data-rfd-draggable-id]",
  "[data-rfd-drag-handle-draggable-id]",
  "button",
  "a",
  "input",
  "textarea",
  "select",
  "[role='button']",
  "[role='menuitem']",
  "[contenteditable='true']",
].join(",");

/**
 * Hold the mouse on the empty part of a board (column background, gaps)
 * and move it to scroll sideways. Cards and controls work as usual.
 */
export const useDragToScroll = (
  ref: RefObject<HTMLElement | null>,
  enabled = true,
) => {
  useEffect(() => {
    const node = ref.current;
    if (!node || !enabled) return;

    let startX = 0;
    let startScroll = 0;
    let pointerId: number | null = null;
    let moved = false;

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || event.pointerType !== "mouse") return;
      const target = event.target as HTMLElement | null;
      if (target?.closest(IGNORE_SELECTOR)) return;
      if (node.scrollWidth <= node.clientWidth) return;
      pointerId = event.pointerId;
      startX = event.clientX;
      startScroll = node.scrollLeft;
      moved = false;
      node.style.cursor = "grabbing";
    };

    const onPointerMove = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return;
      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) < 4) return;
      if (!moved) {
        moved = true;
        node.setPointerCapture(event.pointerId);
        document.body.style.userSelect = "none";
      }
      node.scrollLeft = startScroll - delta;
    };

    const stop = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return;
      if (moved && node.hasPointerCapture(event.pointerId)) {
        node.releasePointerCapture(event.pointerId);
      }
      pointerId = null;
      node.style.cursor = "";
      document.body.style.userSelect = "";
    };

    node.addEventListener("pointerdown", onPointerDown);
    node.addEventListener("pointermove", onPointerMove);
    node.addEventListener("pointerup", stop);
    node.addEventListener("pointercancel", stop);
    return () => {
      node.removeEventListener("pointerdown", onPointerDown);
      node.removeEventListener("pointermove", onPointerMove);
      node.removeEventListener("pointerup", stop);
      node.removeEventListener("pointercancel", stop);
      node.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [ref, enabled]);
};
